import AgendaItem from "@/domain/entities/agenda/AgendaItem";
import AgendaTimeSlot from "@/domain/entities/agenda/AgendaTimeSlot";
import CreateAgendaItemData from "@/domain/entities/agenda/CreateAgendaItemData";
import Email from "@/domain/entities/emails/Email";
import DeepAnalysisError from "./DeepAnalysisError";
import DeepAnalysisResult from "./DeepAnalysisResult";
import type {
    CreateUserVoiceRequestData,
    ReportBugData,
    SearchSimilarTicketsResult,
} from "./TicketTypes";

export enum ComponentDataContentType {
    Mails = "Mails",
    FallBackResponse = "FallBackResponse",
    BasicAiChat = "BasicAiChat",
    Error = "Error",
    AgendaItems = "AgendaItems",
    CreateAgendaItem = "CreateAgendaItem",
    FreeTimeSlots = "FreeTimeSlots",
    CollectBugInfo = "CollectBugInfo",
    CollectVoiceInfo = "CollectVoiceInfo",
    SupportAnswer = "SupportAnswer",
    SearchSimilarTickets = "SearchSimilarTickets",
    ReportBug = "ReportBug",
    CreateUserVoiceRequest = "CreateUserVoiceRequest",
}

export enum ContentAction {
    VIEW = "VIEW",
    DELETE = "DELETE",
    MARKASREAD = "MARKASREAD",
    MARKASUNREAD = "MARKASUNREAD",
    MARKASUSERPROCESSED = "MARKASUSERPROCESSED",
    MARKASUSERUNPROCESSED = "MARKASUSERUNPROCESSED",
    MARKASFOCUS = "MARKASFOCUS",
    MARKASUNFOCUS = "MARKASUNFOCUS",
}

/**
 * Cause of a failed turn (F-1.17), given by the SDK: the thread shows an error bubble with
 * "Retry", except for an exhausted quota.
 */
export type TurnErrorCause = "quota" | "network" | "service";

/**
 * One line of a step-based turn's reasoning: `thought` = the model's own text, `step` = what
 * the assistant did, written by the Api-Ai. Display only.
 */
export type ReasoningItem = { kind: "thought" | "step"; text: string };

/** Step-based contract: the model that wrote an answer, shown under it, and whether it reasoned. */
export type AnswerModel = { name: string; reasoning: boolean };

/**
 * A mail draft prepared by the assistant, in plain text, shown after the answer: the user copies it
 * or opens it in the composer, and sends it himself. `replyTo`: the mail it answers (the composer
 * takes its recipients, subject and thread, as the Reply button does); otherwise a new mail with
 * `subject` and no recipient.
 */
export type AssistantDraft = {
    body: string;
    subject?: string;
    replyTo?: Email;
};

/**
 * A question asked instead of guessing (step-based contract): 1 to 4 choices, the most likely first.
 * `reply`: the message sent when the user clicks the choice, as if he had typed it.
 */
export type AssistantQuestion = {
    question: string;
    choices: { label: string; detail?: string; reply: string }[];
};

/**
 * Step-based contract: the assistant's answer, one per turn (Markdown text) and the elements its refs
 * cite ([m3], [e2], [c1]), by ref. A ref the conversation never read is not here: it stays text.
 * `draft`: the mail draft prepared during the turn, if any.
 */
export type AssistantAnswer = {
    text: string;
    emails: Record<string, Email>;
    /** Refs of the mails of `emails` the user sent: shown by their recipients, not their sender. */
    sentRefs?: string[];
    /** A question with a few choices, shown after the text: a click sends the choice's `reply`. */
    question?: AssistantQuestion;
    /** By ref of `slots`: the message a click on the slot sends (it picks the slot, creates nothing). */
    slotReplies?: Record<string, string>;
    events: Record<string, AgendaItem>;
    slots: Record<string, AgendaTimeSlot>;
    draft?: AssistantDraft;
    /**
     * Names the contacts did not find, searched as written in the addresses (senders, or recipients of
     * sent mails): the app says so under the answer, from its own lookup, not the model's words.
     */
    unknownNames?: { name: string; role: "sender" | "recipient" }[];
};

export type MoreDataContent = Email[] | AgendaTimeSlot[] | AgendaItem[];
export type MoreDataCallbackResult = {
    userRequestId: string;
    content: MoreDataContent;
    moreDataCallBack?: () => Promise<MoreDataCallbackResult>;
};

/** Element union of a MoreDataContent lot + the type guards used to narrow it (centralized here,
 *  alongside MoreDataContent, for the assistant "load more" flow). */
export type MoreDataEntity = Email | AgendaTimeSlot | AgendaItem;

export const isEmailEntity = (item: MoreDataEntity): item is Email =>
    item instanceof Email;

export const isAgendaTimeSlotEntity = (
    item: MoreDataEntity,
): item is AgendaTimeSlot => item instanceof AgendaTimeSlot;

export const isAgendaItemEntity = (item: MoreDataEntity): item is AgendaItem =>
    item instanceof AgendaItem;

type AIResponseProps = {
    userRequestId: string;
    aiComment: string;
    content: {
        contentData: ContentDataTypes;
        contentType: ComponentDataContentType;
        contentAction: ContentAction | undefined;
        upcomingAnalysis: boolean;
        moreDataCallBack?: () => Promise<MoreDataCallbackResult>;
    };
    aiVocalText: string;
    deepAnalysis: {
        result: DeepAnalysisResult | undefined;
        error: DeepAnalysisError | undefined;
    };
    /** Failed turn only: its cause. The SDK kept the turn out of the conversation history. */
    errorCause?: TurnErrorCause;
    /** Reasoning of the turn, in order (step-based contract, when the Api-Ai sends it). */
    reasoning?: ReasoningItem[];
    /**
     * Step-based contract: the answer, shown instead of `aiComment`; `content` then holds the
     * proposal to confirm (mails of an action, an event), shown after it.
     */
    answer?: AssistantAnswer;
    /** Step-based contract: the model that wrote the answer; absent when the code wrote it. */
    answerModel?: AnswerModel;
    /** Step-based contract, answered turn: its id, for the thumbs under the answer. */
    turnId?: string;
};

export type ContentDataTypes =
    | Email[]
    | string
    | AgendaItem[]
    | CreateAgendaItemData
    | AgendaTimeSlot[]
    | ReportBugData
    | CreateUserVoiceRequestData
    | SearchSimilarTicketsResult;

export default class AIResponse {
    userRequestId: string;

    aiComment: string;

    content: {
        contentData: ContentDataTypes;
        contentType: ComponentDataContentType;
        contentAction: ContentAction | undefined;
        upcomingAnalysis: boolean;
        moreDataCallBack?: () => Promise<MoreDataCallbackResult>;
    };

    aiVocalText: string;

    deepAnalysis: {
        result: DeepAnalysisResult | undefined;
        error: DeepAnalysisError | undefined;
    };

    errorCause?: TurnErrorCause;

    reasoning?: ReasoningItem[];

    answer?: AssistantAnswer;

    answerModel?: AnswerModel;

    turnId?: string;

    constructor(props: AIResponseProps) {
        this.userRequestId = props.userRequestId;
        this.aiComment = props.aiComment;
        this.content = props.content;
        this.aiVocalText = props.aiVocalText;
        this.deepAnalysis = props.deepAnalysis;
        this.errorCause = props.errorCause;
        this.reasoning = props.reasoning;
        this.answer = props.answer;
        this.answerModel = props.answerModel;
        this.turnId = props.turnId;
    }

    /** Its analysis was announced and has not come back yet (neither result nor error). */
    get isAnalysisPending(): boolean {
        return (
            this.content.upcomingAnalysis &&
            !this.deepAnalysis.result &&
            !this.deepAnalysis.error
        );
    }
}
